import { getMic } from "@/lib/mic";
import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Square, Loader2, Moon } from "lucide-react";
import { toast } from "sonner";

const FEELS = ["Tough", "Okay", "Good", "I felt different"];

export default function EveningCheckIn({ done, onDone }) {
  const navigate = useNavigate();
  const [feel, setFeel] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [rec, setRec] = useState(false);
  const mr = useRef(null); const chunks = useRef([]);

  const record = async () => {
    if (rec) { mr.current?.stop(); setRec(false); return; }
    try {
      const stream = await getMic();
      const m = new MediaRecorder(stream); mr.current = m; chunks.current = [];
      m.ondataavailable = (e) => chunks.current.push(e.data);
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData(); fd.append("file", new Blob(chunks.current, { type: "audio/webm" }), "a.webm");
        try { const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); setText((t) => `${t} ${data.text || ""}`.trim()); } catch { toast.error("Couldn't hear that."); }
      };
      m.start(); setRec(true);
    } catch { }
  };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/routine/evening", { feeling: feel, text, local_date: new Date().toLocaleDateString("en-CA") }); setRes(data); onDone && onDone(); }
    catch { toast.error("Couldn't save your reflection."); }
    setBusy(false);
  };

  if (done && !res) return null;
  return (
    <section className="mb-12 rounded-3xl border border-indigo-300/15 bg-gradient-to-br from-[#0b1026] to-[#111836] p-6 sm:p-8" data-testid="routine-evening">
      <AnimatePresence mode="wait">
        {res ? (
          <motion.div key="ack" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p className="font-serif text-xl sm:text-2xl leading-snug text-white" data-testid="evening-ack">{res.ack}</p>
            <Button className="rounded-full mt-6" onClick={() => navigate(res.next.route)} data-testid="evening-close-day"><Moon className="w-4 h-4 mr-1.5" /> {res.next.label} →</Button>
          </motion.div>
        ) : !feel ? (
          <motion.div key="feel" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <h2 className="text-xs uppercase tracking-[0.22em] text-indigo-200/90 mb-4">How did today feel?</h2>
            <div className="flex flex-wrap gap-2">
              {FEELS.map((f) => <button key={f} onClick={() => setFeel(f)} data-testid={`evening-feel-${f.split(" ").join("-").toLowerCase()}`} className="px-4 py-2 rounded-full text-sm border border-white/15 text-white/85 hover:border-indigo-300/50 transition-colors">{f}</button>)}
            </div>
          </motion.div>
        ) : (
          <motion.div key="q" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <p className="font-serif text-xl text-white mb-4">Where did you notice yourself showing up differently?</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={rec ? "Listening…" : "Type, or tap the mic and speak…"} className="bg-white/5 border-white/10 text-white" data-testid="evening-text" />
            <div className="flex gap-2 mt-3">
              <Button type="button" variant="outline" size="icon" className="rounded-full border-white/15" onClick={record} data-testid="evening-mic">{rec ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
              <Button className="rounded-full" onClick={submit} disabled={busy} data-testid="evening-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Share with your Coach"}</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
